import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const changeLanguage = useCallback((langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    document.documentElement.lang = langCode;
    localStorage.setItem('i18nextLng', langCode);
  }, [i18n]);

  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Debounce to prevent double-trigger on mobile
    if (timeoutRef.current) return;
    
    setIsOpen(prev => !prev);
    
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
    }, 300);
  }, []);

  const handleSelect = useCallback((langCode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    changeLanguage(langCode);
  }, [changeLanguage]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white transition-colors bg-[#141414] border border-[#2A2A2A] hover:border-[#FFCC00]/50 touch-manipulation"
        data-testid="language-switcher"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm">{currentLang.flag}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute left-0 md:right-0 md:left-auto mt-2 w-44 bg-[#141414] border border-[#2A2A2A] shadow-xl z-[100]"
          role="listbox"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-[#1A1A1A] active:bg-[#252525] transition-colors touch-manipulation ${
                i18n.language === lang.code ? 'text-[#FFCC00] bg-[#1A1A1A]' : 'text-gray-300'
              }`}
              data-testid={`lang-${lang.code}`}
              role="option"
              aria-selected={i18n.language === lang.code}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-base font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
